import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Input, Textarea, FormControl, FormLabel, HStack, VStack, Box, Text, Badge,
  Switch, IconButton, Select, Divider, Tooltip, useToast, Flex, Tabs, TabList, Tab,
  TabPanels, TabPanel, SimpleGrid, Spinner,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiCopy, FiLink, FiDownload, FiEye, FiLock, FiUnlock,
         FiChevronUp, FiChevronDown, FiEdit2, FiGrid } from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';

const FIELD_TYPES = [
  { value: 'text',     label: 'Short Text' },
  { value: 'textarea', label: 'Long Text / Paragraph' },
  { value: 'select',   label: 'Dropdown' },
  { value: 'radio',    label: 'Multiple Choice' },
  { value: 'number',   label: 'Number' },
];

const genId = () => Math.random().toString(36).slice(2, 9);

function FieldEditor({ field, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const needsOptions = field.type === 'select' || field.type === 'radio';
  const [optionsText, setOptionsText] = useState((field.options || []).join('\n'));

  const handleOptionsBlur = () => {
    const opts = optionsText.split('\n').map(s => s.trim()).filter(Boolean);
    onChange({ ...field, options: opts });
  };

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="gray.50">
      <HStack mb={2} align="flex-start">
        <VStack spacing={0} mr={1}>
          <IconButton icon={<FiChevronUp />} size="xs" variant="ghost" isDisabled={isFirst} onClick={onMoveUp} aria-label="Move up" />
          <IconButton icon={<FiChevronDown />} size="xs" variant="ghost" isDisabled={isLast} onClick={onMoveDown} aria-label="Move down" />
        </VStack>
        <Box flex={1}>
          <HStack mb={2}>
            <Input
              size="sm"
              value={field.label}
              onChange={e => onChange({ ...field, label: e.target.value })}
              placeholder="Field label…"
              fontWeight="600"
              flex={1}
            />
            <Select size="sm" value={field.type} onChange={e => onChange({ ...field, type: e.target.value })} w="170px">
              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <Tooltip label="Required field">
              <HStack spacing={1}>
                <Text fontSize="xs" color="gray.500">Req</Text>
                <Switch
                  size="sm"
                  isChecked={!!field.required}
                  onChange={e => onChange({ ...field, required: e.target.checked })}
                />
              </HStack>
            </Tooltip>
            <Tooltip label="Include this field's answer in the prospect's notes">
              <HStack spacing={1}>
                <Text fontSize="xs" color="gray.500">Notes</Text>
                <Switch
                  size="sm"
                  colorScheme="purple"
                  isChecked={!!field.include_in_notes}
                  onChange={e => onChange({ ...field, include_in_notes: e.target.checked })}
                />
              </HStack>
            </Tooltip>
          </HStack>
          {(field.type === 'text' || field.type === 'textarea' || field.type === 'number') && (
            <Input
              size="sm"
              value={field.placeholder || ''}
              onChange={e => onChange({ ...field, placeholder: e.target.value })}
              placeholder="Placeholder text (optional)…"
            />
          )}
          {needsOptions && (
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Options (one per line)</Text>
              <Textarea
                size="sm"
                value={optionsText}
                onChange={e => setOptionsText(e.target.value)}
                onBlur={handleOptionsBlur}
                placeholder={"Option 1\nOption 2\nOption 3"}
                rows={3}
              />
            </Box>
          )}
        </Box>
        <IconButton icon={<FiTrash2 />} size="xs" colorScheme="red" variant="ghost" onClick={onDelete} aria-label="Delete field" />
      </HStack>
    </Box>
  );
}

export default function KioskFormBuilder({ isOpen, onClose, campaignId, campaignName, editForm, onSaved }) {
  const toast = useToast();
  const isEdit = !!editForm;

  const [saving,   setSaving]   = useState(false);
  const [title,    setTitle]    = useState('');
  const [desc,     setDesc]     = useState('');
  const [color,    setColor]    = useState('#EE7203');
  const [qrOn,     setQrOn]     = useState(true);
  const [showCo,   setShowCo]   = useState(true);
  const [showPh,   setShowPh]   = useState(true);
  const [showEm,   setShowEm]   = useState(true);
  const [fields,   setFields]   = useState([]);
  const [notesTemplate, setNotesTemplate] = useState('');
  const [tabIdx,   setTabIdx]   = useState(0);

  // Password management
  const [pwMode,    setPwMode]   = useState('none'); // 'none'|'set'|'change'
  const [newPw,     setNewPw]    = useState('');
  const [pwSaving,  setPwSaving] = useState(false);
  const [isProtected, setIsProtected] = useState(false);

  // Duplicate modal
  const [dupOpen,   setDupOpen]  = useState(false);
  const [dupSuffix, setDupSuffix]= useState('');
  const [dupSaving, setDupSaving]= useState(false);

  // Submissions
  const [submissions, setSubmissions] = useState([]);
  const [subLoading,  setSubLoading]  = useState(false);

  // QR image
  const [qrDataUrl, setQrDataUrl] = useState(null);

  const kioskUrl = editForm?.token
    ? `${window.location.origin}/prospect-kiosk/${editForm.token}`
    : null;

  // Initialise from editForm
  useEffect(() => {
    if (!isOpen) return;
    if (editForm) {
      setTitle(editForm.title || '');
      setDesc(editForm.description || '');
      setColor(editForm.branding_color || '#EE7203');
      setQrOn(editForm.qr_enabled !== false);
      setShowCo(editForm.show_company !== false);
      setShowPh(editForm.show_phone !== false);
      setShowEm(editForm.show_email !== false);
      setFields(editForm.custom_fields || []);
      setNotesTemplate(editForm.notes_template || '');
      setIsProtected(!!editForm.password_protected);
    } else {
      setTitle('Trade Show Sign Up');
      setDesc('');
      setColor('#EE7203');
      setQrOn(true); setShowCo(true); setShowPh(true); setShowEm(true);
      setFields([]);
      setNotesTemplate('');
      setIsProtected(false);
    }
    setPwMode('none'); setNewPw('');
    setTabIdx(0);
    setSubmissions([]); setQrDataUrl(null);
  }, [isOpen, editForm]);

  // Load QR image when on QR tab
  useEffect(() => {
    if (tabIdx === 2 && editForm?.token && !qrDataUrl) {
      api.get(`/kiosk-forms/${editForm.token}/qr`, { responseType: 'blob' })
        .then(r => setQrDataUrl(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [tabIdx, editForm, qrDataUrl]);

  // Load submissions when on submissions tab
  useEffect(() => {
    if (tabIdx === 3 && editForm?.token) {
      setSubLoading(true);
      api.get(`/kiosk-forms/${editForm.token}/submissions`)
        .then(r => setSubmissions(r.data))
        .catch(() => {})
        .finally(() => setSubLoading(false));
    }
  }, [tabIdx, editForm]);

  const addField = () => {
    setFields(f => [...f, { id: genId(), label: '', type: 'text', required: false, include_in_notes: false, placeholder: '', options: [] }]);
  };

  const updateField = (idx, updated) => setFields(f => f.map((x, i) => i === idx ? updated : x));
  const deleteField = (idx) => setFields(f => f.filter((_, i) => i !== idx));
  const moveField   = (idx, dir) => {
    const arr = [...fields];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setFields(arr);
  };

  const handleSave = async () => {
    if (!title.trim()) return toast({ title: 'Title is required', status: 'warning', duration: 2000 });
    setSaving(true);
    try {
      const payload = {
        campaign_id: campaignId,
        title: title.trim(),
        description: desc.trim() || null,
        custom_fields: fields.filter(f => f.label.trim()),
        notes_template: notesTemplate.trim() || null,
        qr_enabled: qrOn,
        show_company: showCo,
        show_phone: showPh,
        show_email: showEm,
        branding_color: color,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/kiosk-forms/${editForm.token}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/kiosk-forms', payload);
        saved = data;
      }
      toast({ title: isEdit ? 'Form updated' : 'Kiosk form created', status: 'success', duration: 2000 });
      onSaved && onSaved(saved);
      if (!isEdit) onClose();
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Save failed', status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  const handleSetPassword = async () => {
    if (!newPw.trim()) return;
    setPwSaving(true);
    try {
      await api.patch(`/kiosk-forms/${editForm.token}/password`, { password: newPw.trim() });
      setIsProtected(true);
      setPwMode('none'); setNewPw('');
      toast({ title: 'Password set', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: 'Failed to set password', status: 'error', duration: 2000 }); }
    setPwSaving(false);
  };

  const handleRemovePassword = async () => {
    setPwSaving(true);
    try {
      await api.patch(`/kiosk-forms/${editForm.token}/password`, { password: '' });
      setIsProtected(false);
      setPwMode('none');
      toast({ title: 'Password removed', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setPwSaving(false);
  };

  const handleDuplicate = async () => {
    setDupSaving(true);
    try {
      const { data } = await api.post(`/kiosk-forms/${editForm.token}/duplicate`, { suffix: dupSuffix.trim() || undefined });
      toast({ title: 'Form duplicated', status: 'success', duration: 2000 });
      setDupOpen(false); setDupSuffix('');
      onSaved && onSaved(data);
    } catch(e) { toast({ title: 'Duplicate failed', status: 'error', duration: 2000 }); }
    setDupSaving(false);
  };

  const copyUrl = () => {
    if (!kioskUrl) return;
    navigator.clipboard.writeText(kioskUrl).then(() =>
      toast({ title: 'URL copied to clipboard', status: 'success', duration: 1500 }));
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `kiosk-qr-${editForm.token.slice(0, 8)}.png`;
    a.click();
  };

  const downloadCsv = () => {
    if (!editForm?.token) return;
    window.open(`/api/kiosk-forms/${editForm.token}/csv`, '_blank');
  };

  // Build the header-right actions (duplicate + preview) for the PageShell
  const headerRight = (
    <HStack spacing={1}>
      {isEdit && (
        <>
          <Tooltip label="Duplicate this form">
            <IconButton icon={<FiCopy />} size="sm" variant="ghost" onClick={() => setDupOpen(true)} aria-label="Duplicate" />
          </Tooltip>
          <Tooltip label="Preview kiosk">
            <IconButton icon={<FiEye />} size="sm" variant="ghost" onClick={() => window.open(kioskUrl, '_blank')} aria-label="Preview" />
          </Tooltip>
        </>
      )}
      <Button size="sm" variant="ghost" onClick={onClose} ml={2}>Cancel</Button>
      <Button size="sm" colorScheme="orange" onClick={handleSave} isLoading={saving}>
        {isEdit ? 'Save Changes' : 'Create Form'}
      </Button>
    </HStack>
  );

  if (!isOpen) return null;

  const titleText = `${isEdit ? 'Edit' : 'Create'} Kiosk Form${campaignName ? ' — ' + campaignName : ''}`;

  return (
    <>
    <PageShell
      fullPage
      onBack={onClose}
      title={titleText}
      headerRight={headerRight}
    >
      <Box>
        <Tabs index={tabIdx} onChange={setTabIdx} isLazy>
          <TabList px={4} borderBottom="1px solid" borderColor="gray.100" position="sticky" top={0} bg="white" zIndex={1}>
            <Tab fontSize="sm">Form Setup</Tab>
            <Tab fontSize="sm">Custom Fields</Tab>
            {isEdit && <Tab fontSize="sm">QR & URL</Tab>}
            {isEdit && <Tab fontSize="sm">Submissions {submissions.length > 0 && `(${submissions.length})`}</Tab>}
          </TabList>

          <TabPanels>
              {/* ── TAB 1: FORM SETUP ───────────────────────────────── */}
              <TabPanel p={5}>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600">Form Title *</FormLabel>
                    <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. ExpoFair 2026 Sign Up" />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600">Description / Instructions</FormLabel>
                    <Textarea value={desc} onChange={e => setDesc(e.target.value)}
                      placeholder="Optional intro text shown on the kiosk…" rows={2} />
                  </FormControl>

                  <HStack spacing={6} align="flex-start">
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="600">Brand Colour</FormLabel>
                      <HStack>
                        <input type="color" value={color} onChange={e => setColor(e.target.value)}
                          style={{ width: 42, height: 36, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                        <Input value={color} onChange={e => setColor(e.target.value)} size="sm" w="110px" fontFamily="mono" />
                      </HStack>
                    </FormControl>
                  </HStack>

                  <Divider />
                  <Text fontWeight="700" fontSize="sm" color="gray.600">Standard Fields</Text>
                  <SimpleGrid columns={2} spacing={3}>
                    <HStack justify="space-between" bg="gray.50" p={3} borderRadius="md">
                      <Box>
                        <Text fontSize="sm" fontWeight="600">Company Name</Text>
                        <Text fontSize="xs" color="gray.500">Always shown (required)</Text>
                      </Box>
                      <Switch isChecked isDisabled />
                    </HStack>
                    <HStack justify="space-between" bg="gray.50" p={3} borderRadius="md">
                      <Box>
                        <Text fontSize="sm" fontWeight="600">Contact Name</Text>
                        <Text fontSize="xs" color="gray.500">Always shown</Text>
                      </Box>
                      <Switch isChecked isDisabled />
                    </HStack>
                    <HStack justify="space-between" bg="gray.50" p={3} borderRadius="md">
                      <Box>
                        <Text fontSize="sm" fontWeight="600">Email Address</Text>
                      </Box>
                      <Switch isChecked={showEm} onChange={e => setShowEm(e.target.checked)} />
                    </HStack>
                    <HStack justify="space-between" bg="gray.50" p={3} borderRadius="md">
                      <Box>
                        <Text fontSize="sm" fontWeight="600">Phone Number</Text>
                      </Box>
                      <Switch isChecked={showPh} onChange={e => setShowPh(e.target.checked)} />
                    </HStack>
                  </SimpleGrid>

                  <Divider />
                  <Text fontWeight="700" fontSize="sm" color="gray.600">Options</Text>
                  <HStack justify="space-between" bg="blue.50" p={3} borderRadius="md">
                    <Box>
                      <Text fontSize="sm" fontWeight="600">Show QR Code on kiosk screen</Text>
                      <Text fontSize="xs" color="gray.500">Visitors can scan to fill in on their phone</Text>
                    </Box>
                    <Switch isChecked={qrOn} onChange={e => setQrOn(e.target.checked)} colorScheme="blue" />
                  </HStack>

                  {/* Password section (edit only) */}
                  {isEdit && (
                    <>
                      <Divider />
                      <Text fontWeight="700" fontSize="sm" color="gray.600">Password Protection</Text>
                      <Box bg={isProtected ? 'orange.50' : 'gray.50'} p={3} borderRadius="md" border="1px solid" borderColor={isProtected ? 'orange.200' : 'gray.200'}>
                        <HStack justify="space-between" mb={isProtected && pwMode === 'none' ? 0 : 2}>
                          <HStack>
                            {isProtected ? <FiLock color="#DD6B20" /> : <FiUnlock color="#718096" />}
                            <Box>
                              <Text fontSize="sm" fontWeight="600">{isProtected ? 'Password Protected' : 'No Password'}</Text>
                              <Text fontSize="xs" color="gray.500">
                                {isProtected ? 'A password is required to access this form' : 'Anyone with the URL can access this form'}
                              </Text>
                            </Box>
                          </HStack>
                          <HStack>
                            {isProtected && pwMode === 'none' && (
                              <>
                                <Button size="xs" variant="outline" onClick={() => setPwMode('change')}>Change</Button>
                                <Button size="xs" colorScheme="red" variant="ghost" onClick={handleRemovePassword} isLoading={pwSaving}>Remove</Button>
                              </>
                            )}
                            {!isProtected && pwMode === 'none' && (
                              <Button size="xs" colorScheme="orange" variant="outline" onClick={() => setPwMode('set')}>Set Password</Button>
                            )}
                          </HStack>
                        </HStack>
                        {(pwMode === 'set' || pwMode === 'change') && (
                          <HStack mt={2}>
                            <Input
                              size="sm"
                              type="password"
                              value={newPw}
                              onChange={e => setNewPw(e.target.value)}
                              placeholder="Enter new password…"
                              onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
                            />
                            <Button size="sm" colorScheme="orange" onClick={handleSetPassword} isLoading={pwSaving} isDisabled={!newPw.trim()}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setPwMode('none'); setNewPw(''); }}>Cancel</Button>
                          </HStack>
                        )}
                      </Box>
                    </>
                  )}
                </VStack>
              </TabPanel>

              {/* ── TAB 2: CUSTOM FIELDS ───────────────────────────── */}
              <TabPanel p={5}>
                <VStack spacing={3} align="stretch">
                  {fields.length === 0 && (
                    <Box textAlign="center" py={8} color="gray.400">
                      <Text fontSize="sm">No custom fields yet.</Text>
                      <Text fontSize="xs">Add fields below to capture additional information from visitors.</Text>
                    </Box>
                  )}
                  {fields.map((field, idx) => (
                    <FieldEditor
                      key={field.id}
                      field={field}
                      onChange={updated => updateField(idx, updated)}
                      onDelete={() => deleteField(idx)}
                      onMoveUp={() => moveField(idx, -1)}
                      onMoveDown={() => moveField(idx, 1)}
                      isFirst={idx === 0}
                      isLast={idx === fields.length - 1}
                    />
                  ))}
                  <Button leftIcon={<FiPlus />} variant="dashed" borderStyle="dashed" border="2px dashed"
                    borderColor="gray.300" colorScheme="gray" onClick={addField} size="sm" mt={2}>
                    Add Field
                  </Button>

                  {/* ── Prospect notes section ─────────────────────── */}
                  <Divider mt={4} />
                  <Box bg="purple.50" p={4} borderRadius="md" border="1px solid" borderColor="purple.100">
                    <Text fontWeight="700" fontSize="sm" color="purple.700" mb={1}>Prospect Notes</Text>
                    <Text fontSize="xs" color="gray.600" mb={3}>
                      Submitted answers can be written to the prospect's Notes automatically. Use the <b>Notes</b>{' '}
                      switch on each field to include it, or write a custom template below — a template overrides
                      the per-field switches.
                    </Text>
                    <FormLabel fontSize="xs" fontWeight="600" color="gray.600">Notes Template (optional)</FormLabel>
                    <Textarea
                      size="sm"
                      fontFamily="mono"
                      rows={4}
                      value={notesTemplate}
                      onChange={e => setNotesTemplate(e.target.value)}
                      placeholder={"Enquiry from {company_name} ({contact_name})\nRequirement: {" + (fields[0]?.id || 'field_id') + "}"}
                    />
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Placeholders:&nbsp;
                      <code>{'{company_name}'}</code>,&nbsp;
                      <code>{'{contact_name}'}</code>,&nbsp;
                      <code>{'{email}'}</code>,&nbsp;
                      <code>{'{phone}'}</code>
                      {fields.filter(f => f.label.trim()).length > 0 && (
                        <>
                          {', and per-field: '}
                          {fields.filter(f => f.label.trim()).map((f, i, arr) => (
                            <React.Fragment key={f.id}>
                              <code>{`{${f.id}}`}</code>
                              <Text as="span" color="gray.400"> ({f.label})</Text>
                              {i < arr.length - 1 ? ', ' : ''}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>

              {/* ── TAB 3: QR & URL ────────────────────────────────── */}
              {isEdit && (
                <TabPanel p={5}>
                  <VStack spacing={5} align="stretch">
                    <Box>
                      <Text fontWeight="700" fontSize="sm" color="gray.600" mb={2}>Kiosk URL</Text>
                      <HStack>
                        <Input value={kioskUrl || ''} isReadOnly fontSize="sm" fontFamily="mono" bg="gray.50" />
                        <Tooltip label="Copy URL">
                          <IconButton icon={<FiLink />} onClick={copyUrl} aria-label="Copy" />
                        </Tooltip>
                        <Tooltip label="Open in new tab">
                          <IconButton icon={<FiEye />} onClick={() => window.open(kioskUrl, '_blank')} aria-label="Open" />
                        </Tooltip>
                      </HStack>
                    </Box>

                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="700" fontSize="sm" color="gray.600">QR Code</Text>
                        {qrDataUrl && (
                          <Button size="xs" leftIcon={<FiDownload />} variant="outline" onClick={downloadQr}>
                            Download PNG
                          </Button>
                        )}
                      </HStack>
                      <Box bg="gray.50" borderRadius="xl" p={6} textAlign="center" border="1px solid" borderColor="gray.200">
                        {qrDataUrl ? (
                          <img src={qrDataUrl} alt="QR Code" style={{ width: 220, height: 220, display: 'inline-block', borderRadius: 8 }} />
                        ) : (
                          <Spinner color="gray.400" />
                        )}
                        <Text fontSize="xs" color="gray.400" mt={3}>
                          Scan to access the kiosk form on a mobile device.<br />
                          Print or display alongside the kiosk station.
                        </Text>
                      </Box>
                    </Box>

                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="700" fontSize="sm" color="gray.600">CSV Data Export</Text>
                        <Button size="xs" leftIcon={<FiDownload />} colorScheme="green" variant="outline" onClick={downloadCsv}>
                          Download CSV
                        </Button>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        Downloads all submissions for this form as a CSV file, including all custom field responses.
                      </Text>
                    </Box>
                  </VStack>
                </TabPanel>
              )}

              {/* ── TAB 4: SUBMISSIONS ─────────────────────────────── */}
              {isEdit && (
                <TabPanel p={5}>
                  {subLoading ? (
                    <Flex justify="center" py={8}><Spinner /></Flex>
                  ) : submissions.length === 0 ? (
                    <Box textAlign="center" py={8} color="gray.400">
                      <Text fontSize="sm">No submissions yet.</Text>
                      <Text fontSize="xs">Once visitors complete the form, their entries will appear here.</Text>
                    </Box>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="sm" color="gray.500">{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</Text>
                        <Button size="xs" leftIcon={<FiDownload />} colorScheme="green" variant="outline" onClick={downloadCsv}>CSV</Button>
                      </HStack>
                      {submissions.map(s => {
                        const d = s.submission_data || {};
                        return (
                          <Box key={s.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="white">
                            <HStack justify="space-between" mb={1}>
                              <Text fontWeight="700" fontSize="sm">{s.company_name || d.company_name || '—'}</Text>
                              <Text fontSize="xs" color="gray.400">
                                {new Date(s.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </Text>
                            </HStack>
                            {(s.first_name || d.contact_name) && (
                              <Text fontSize="xs" color="gray.600">{s.first_name ? `${s.first_name} ${s.last_name || ''}`.trim() : d.contact_name}</Text>
                            )}
                            {(s.contact_email || d.email) && <Text fontSize="xs" color="blue.500">{s.contact_email || d.email}</Text>}
                            {d.phone && <Text fontSize="xs" color="gray.500">{d.phone}</Text>}
                            {Object.entries(d).filter(([k]) => !['company_name','contact_name','email','phone'].includes(k)).map(([k, v]) => (
                              <Text key={k} fontSize="xs" color="gray.600" mt={0.5}><strong>{k}:</strong> {v}</Text>
                            ))}
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>

        {/* Footer with token display (for edit mode) */}
        {isEdit && (
          <Box px={5} py={3} borderTop="1px solid" borderColor="gray.100" bg="gray.50">
            <Text fontSize="xs" color="gray.400">Token: <code>{editForm?.token?.slice(0, 12)}…</code></Text>
          </Box>
        )}
      </Box>
    </PageShell>

    {/* Duplicate modal */}
    <Modal isOpen={dupOpen} onClose={() => setDupOpen(false)} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Duplicate Form</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel fontSize="sm">New form label / suffix (optional)</FormLabel>
            <Input
              value={dupSuffix}
              onChange={e => setDupSuffix(e.target.value)}
              placeholder="e.g. Day 2, Stand B, morning session…"
              onKeyDown={e => e.key === 'Enter' && handleDuplicate()}
              autoFocus
            />
            <Text fontSize="xs" color="gray.400" mt={1}>
              The new form will be called "{title}{dupSuffix ? ` - ${dupSuffix}` : ' (Copy)'}" with a fresh unique URL.
            </Text>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={() => setDupOpen(false)} size="sm">Cancel</Button>
            <Button colorScheme="purple" onClick={handleDuplicate} isLoading={dupSaving} size="sm">Duplicate</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}
